import { useState, useEffect, useRef } from 'react';
import { Combobox, InputBase, useCombobox, Text, Group, Button } from '@mantine/core';
import classes from './CompanyAutocomplete.module.css';
import { useDebounce } from '@ayocore/platform/client/hooks/useDebounce';
import { usePartnerRepository } from '@ayocore/erp/crm/client/rpc';
import type { Partner } from '@ayocore/erp/crm/schemas/partner.schema';
import logger from '@ayocore/platform/common/helpers/logger';

function PartnerOptionRow({ label, onSelect }: { label: string; onSelect: () => void }) {
  return (
    <Group justify='space-between' wrap='nowrap' className={classes.optionRow}>
      <Text size='xs' style={{ flex: 1, minWidth: 0 }} truncate>
        {label}
      </Text>
      <Button
        size='compact-xs'
        variant='light'
        className={classes.optionButton}
        tabIndex={-1}
        onMouseDown={e => {
          e.preventDefault();
          onSelect();
        }}
      >
        Выбрать
      </Button>
    </Group>
  );
}

/**
 * Компонент автодополнения для поиска компаний с поддержкой двух источников данных.
 *
 * **Принцип работы:**
 *
 * 1. **Двухсекционный поиск:**
 *    - При вводе текста (минимум 2 символа) выполняется параллельный поиск в двух источниках:
 *      - Внутренняя база данных (через `PartnerRepository.search()`) - до 5 результатов
 *      - Внешний API DaData (через `searchCompany()`) - стандартное количество результатов
 *
 * 2. **Отображение результатов:**
 *    - Результаты отображаются в двух отдельных секциях dropdown:
 *      - Верхняя секция: "Возможно этот партнер уже есть в базе" - партнеры из БД с бейджем "В базе"
 *      - Нижняя секция: "Из внешних источников" - компании из внешнего API
 *    - Для внешних источников длинный текст обрезается с многоточием (не переносится)
 *
 * 3. **Обработка выбора:**
 *    - При выборе партнера из БД:
 *      - если передан `onPartnerSelect`, вызывается он и выбор считается завершённым;
 *      - иначе остаётся только информационное логирование.
 *    - При выборе компании из внешнего источника: вызывается `onCompanySelect` с данными компании,
 *      форма заполняется данными выбранной компании.
 *
 * 4. **Оптимизация:**
 *    - Используется debounce (300ms) для уменьшения количества запросов при вводе
 *    - Dropdown автоматически открывается при появлении результатов
 *    - Загрузка отображается индикатором в правой части input
 *
 * @param value - Текущее значение input
 * @param onChange - Callback при изменении значения input
 * @param onCompanySelect - Callback при выборе компании из внешнего источника
 * @param onPartnerSelect - Callback при выборе партнера из базы
 * @param placeholder - Плейсхолдер для input
 * @param label - Лейбл для input
 * @param error - Текст ошибки для отображения
 * @param disabled - Флаг отключения компонента
 */
interface CompanyAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPartnerSelect?: (partner: Partner) => void;
  onOpenSearch?: () => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  size?: string;
}

export function CompanyAutocomplete({
  value,
  onChange,
  onPartnerSelect,
  onOpenSearch,
  placeholder = 'Начните вводить название компании...',
  label = 'Название компании',
  error,
  disabled = false,
  size = 'xs',
}: CompanyAutocompleteProps) {
  const [dbResults, setDbResults] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedValue = useDebounce(value, 300);
  const partnerRepository = usePartnerRepository();
  const inputRef = useRef<HTMLInputElement>(null);

  // Принудительно отключаем автозаполнение браузера после маунта,
  // т.к. Combobox.Target перезаписывает autocomplete="off" и Chrome его игнорирует
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute('autocomplete', 'new-password');
    }
  }, []);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
    },
  });

  // Поиск в базе партнёров
  useEffect(() => {
    if (!debouncedValue || debouncedValue.length < 2) {
      setDbResults([]);
      return;
    }

    const searchDb = async () => {
      setLoading(true);
      try {
        const dbPartners = (await partnerRepository
          .search(debouncedValue)
          .fetch()) as unknown as Partner[];
        setDbResults(Array.isArray(dbPartners) ? dbPartners : []);
      } catch (error) {
        logger.error('Error searching partners:', error);
        setDbResults([]);
      } finally {
        setLoading(false);
      }
    };

    searchDb();
  }, [debouncedValue]);

  // Handle selection from dropdown
  const handleOptionSubmit = (optionValue: string) => {
    const [type, indexStr] = optionValue.split(':');
    const index = parseInt(indexStr, 10);

    if (type === 'db' && dbResults[index]) {
      const partner = dbResults[index];
      if (onPartnerSelect) {
        onPartnerSelect(partner);
      } else {
        logger.info('Selected partner from DB:', partner);
      }
      combobox.closeDropdown();
    }
  };

  // Handle manual input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    onChange(newValue);
    // Открываем дропдаун при вводе, если введено ≥ 2 символов, иначе закрываем
    if (newValue.length >= 2) {
      if (!combobox.dropdownOpened) {
        combobox.openDropdown();
      }
    } else {
      combobox.closeDropdown();
    }
  };

  // Format partner for display
  const formatPartnerLabel = (partner: Partner) => {
    const parts: string[] = [];
    if (partner.name) parts.push(partner.name);
    if (partner.inn) parts.push(`ИНН: ${partner.inn}`);
    if (partner.address) parts.push(partner.address);
    return parts.join(' • ') || 'Партнер без названия';
  };

  const hasResults = dbResults.length > 0;
  const shouldShowDropdown = combobox.dropdownOpened && hasResults;

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSubmit} withinPortal={true}>
      <Combobox.Target>
        <InputBase
          ref={inputRef}
          component='input'
          type='search'
          size={size}
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            if (value.length >= 2 && (dbResults.length > 0 || loading)) {
              combobox.openDropdown();
            }
          }}
          onClick={() => {
            if (value.length >= 2 && (dbResults.length > 0 || loading)) {
              combobox.openDropdown();
            }
          }}
          autoComplete='new-password'
          data-form-type='other'
          data-lpignore='true'
          error={error}
          disabled={disabled}
          rightSection={
            onOpenSearch ? (
              <Button variant='subtle' size='compact-sm' px={6} onClick={onOpenSearch}>
                Найти
              </Button>
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={onOpenSearch ? 'all' : 'none'}
          rightSectionWidth={onOpenSearch ? 70 : undefined}
        />
      </Combobox.Target>

      {shouldShowDropdown && (
        <Combobox.Dropdown>
          <Combobox.Options>
            {/* Database results section */}
            {dbResults.length > 0 && (
              <>
                <Combobox.Header>
                  <Group justify='space-between' px='xs' py={4}>
                    <Text size='xs' fw={600} c='dimmed'>
                      Похожие партнеры
                    </Text>
                    <Button
                      size='compact-xs'
                      variant='subtle'
                      c='dimmed'
                      onMouseDown={e => {
                        e.preventDefault();
                        combobox.closeDropdown();
                      }}
                    >
                      Закрыть
                    </Button>
                  </Group>
                </Combobox.Header>
                {dbResults.map((partner, index) => (
                  <Combobox.Option
                    key={`db:${index}`}
                    value={`db:${index}`}
                    style={{ cursor: 'pointer' }}
                  >
                    <PartnerOptionRow
                      label={formatPartnerLabel(partner)}
                      onSelect={() => {
                        if (onPartnerSelect) onPartnerSelect(partner);
                        combobox.closeDropdown();
                      }}
                    />
                  </Combobox.Option>
                ))}
              </>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      )}
    </Combobox>
  );
}

export default CompanyAutocomplete;
